import useRosterConfig from "@/hooks/useRosterConfig";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import TableBody from "../rosterTable/TableBody";
import "@css/modal_submittedWantedList.css";
import type * as type from "@vTypes";
import type { NurseResponse } from "@/types/nurse-type";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import RosterMadeTable from "../rosterTable/RosterMadeTable";
import { RosterViolationFloatingControls } from "../rosterTable/RosterViolationFloatingControls";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth.ts";
import { useIsHeadNurse } from "@/hooks/useHeadNurse";
import { useGroupStore } from "@store/groupStore.ts";
import {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  Activity,
  type MouseEvent,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import httpClient from "@/config/axios";
import { toast } from "@/components/toast/CustomToast";
import ModalRosterModify from "./ModalRosterModify";
import ActionButton from "@/components/common/ActionButton";
import { MonthlyMemoPostIt } from "./MonthlyMemoPostIt";
import {
  useWantedAdjustmentQuery,
  type WantedAdjustmentData,
  type BannedWantedAdjustmentEntry,
  type WantedAdjustmentEntry,
  type WantedAdjustmentNurse,
} from "@/hooks/roster-craete/useWantedAdjustmentQuery";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import { useRosterValidation } from "@/hooks/useRosterValidation";
import { getPrecepteePolicy } from "@/utils/precepteePolicy";
import { cn } from "@/lib/utils";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { resolveShiftFromWantedEntry } from "@/utils/wanted-shift-util";
import { sortNurseListByRoleThenSequence } from "@utils/common-util.ts";
import type { InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useRosterFocusStore } from "@/store/rosterFocusStore";
import { useRosterCreateNurseReorder } from "@/pages/roster-create/hook/useRosterCreateNurseReorder";
import { buildRosterTableLayoutConfig } from "@/components/nurse/rosterColumnHoverLayout";
import { RosterCreateTableHeader } from "@/pages/roster-create/components/RosterCreateTableHeader";
import useGroupMembers from "@/hooks/useGroupMembers";
import { filterWantedBoardMonthlyNurses } from "./utils/filterWantedBoardMonthlyNurses";
import { mergeWantedEntryOverrides } from "./utils/mergeWantedEntryOverrides";
import { toggleBannedEntryApplied } from "./utils/toggleBannedEntryApplied";
import { toggleBannedShiftIds } from "./utils/toggleBannedShiftIds";
import { withoutBannedShiftsOnAppliedFixedDays } from "./utils/withoutBannedShiftsOnAppliedFixedDays";
import { useGroupMonthlyMemoQuery } from "@/hooks/roster-craete/useGroupMonthlyMemoQuery";
import { buildGroupMonthlyMemoByNurse } from "./utils/groupMonthlyMemo";
import { ModifiedCellsProvider } from "@/pages/roster-create/context/ModifiedCellsContext";
import type { GroupMemberInMonth } from "@/api/nurses";

type WantedAdjustmentToolMode = "assignment" | "banned";

const WANTED_BOARD_MODIFIED_CELLS = {
  modifiedCells: new Set<string>(),
};

type WantedAdjustmentSaveResponse = {
  readonly warnings?: readonly { readonly reason?: string }[];
};

function LoadingOverlay({
  className,
  message,
}: {
  className?: string;
  message: string;
}) {
  return (
    <div
      className={cn(
        "absolute pointer-events-auto inset-0 z-[100] flex items-center justify-center bg-white/50",
        className
      )}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div className="bg-white p-6 px-10 rounded-lg shadow-2xl">
        <LoadingSpiner className="font-pretendard! text-base" msg={message} />
      </div>
    </div>
  );
}

function WantedSettingSwitchRow({
  label,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 min-w-[200px] sm:min-w-0 sm:flex-1 sm:max-w-[240px]",
        disabled && "opacity-50"
      )}
    >
      <span className="text-sm! text-zinc-700 whitespace-nowrap shrink-0">
        {label}
      </span>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-zinc-300"
        aria-label={label}
      />
    </div>
  );
}

function ModalRosterWanted({
  defaultShiftCode,
  currentScheduleInfo,
  holidayDates,
  nurses,
  shiftInfo,
  onClose,
  isViewOnlyOff,
  showManpowerButton,
  manPower_monthly,
  manPower_daily,
  memo,
  setMemo,
  isEditableMemo,
  currentScheduleID,
  prevTailData,
}: {
  defaultShiftCode: type.availableShifts[];
  currentScheduleInfo: { year: number; month: number; version: number };
  holidayDates: string[] | null;
  nurses: NurseResponse[] | null;
  shiftInfo: type.availableShifts[];
  onClose: () => void;
  isViewOnlyOff: boolean;
  showManpowerButton: boolean;
  manPower_monthly: type.ManPowerMonthly | null;
  manPower_daily?: type.ManPowerDaily | null;
  memo: string;
  setMemo?: React.Dispatch<React.SetStateAction<string>>;
  isEditableMemo: boolean;
  currentScheduleID: string | undefined;
  prevTailData?: PrevTailData | null;
  initialTab?: "view" | "settings";
}) {
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const isHeadNurse = useIsHeadNurse();
  const loginUser = useLoginUser();
  const { selectedGroupId } = useGroupStore();
  const isDragEditing = false;
  const wantedBoardGroupId = isAdmin
    ? (selectedGroupId ?? null)
    : (loginUser?.group_id ?? null);

  const gid = isAdmin ? (selectedGroupId ?? "") : "";
  const { data: shiftsData } = useShiftsQuery(gid || undefined);
  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    defaultShiftCode,
    shiftsData ?? null
  );
  const { data: wantedBoardMembers } = useGroupMembers(
    wantedBoardGroupId,
    currentScheduleInfo.year,
    currentScheduleInfo.month
  );
  const wantedBoardMonthlyNurses = useMemo(
    () => filterWantedBoardMonthlyNurses(nurses, wantedBoardMembers?.members),
    [nurses, wantedBoardMembers?.members]
  );
  const wantedBoardMemberStatusMap = useMemo<
    ReadonlyMap<string, GroupMemberInMonth> | undefined
  >(() => {
    if (!wantedBoardMembers) return undefined;
    return new Map(
      wantedBoardMembers.members.map((member) => [member.nurse_id, member])
    );
  }, [wantedBoardMembers]);
  const { data: groupMonthlyMemoData } = useGroupMonthlyMemoQuery(
    currentScheduleInfo.year,
    currentScheduleInfo.month,
    wantedBoardGroupId,
    Boolean(isAdmin || isHeadNurse)
  );
  const groupMonthlyMemos = groupMonthlyMemoData?.memos ?? [];
  const monthlyMemoByNurse = useMemo(
    () => buildGroupMonthlyMemoByNurse(groupMonthlyMemos),
    [groupMonthlyMemos]
  );

  // 모달이 닫힐 때 진행 중인 wanted/adjustment 쿼리 취소
  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 쿼리 취소
      if (currentScheduleInfo?.year && currentScheduleInfo?.month) {
        queryClient.cancelQueries({
          queryKey: QUERY_KEYS.wanted.adjustment(
            currentScheduleInfo.year,
            currentScheduleInfo.month
          ),
        });
      }
    };
  }, [queryClient, currentScheduleInfo?.year, currentScheduleInfo?.month]);

  // 초기 데이터 저장 (초기화용)
  const [initialWantedAdjustmentData, setInitialWantedAdjustmentData] =
    useState<WantedAdjustmentData | null>(null);

  // 로컬에서 변경된 entries 관리 (nurse_id + shift_date를 키로 사용)
  const [modifiedEntries, setModifiedEntries] = useState<
    Map<string, WantedAdjustmentEntry | null>
  >(new Map());

  // 설정완료 확인 모달 상태
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [, setIsRosterMadeSummaryMinimized] = useState(false);
  const [showPerShiftStats, setShowPerShiftStats] = useState(false);

  // 닫기 확인 모달 상태
  const [isCloseConfirmModalOpen, setIsCloseConfirmModalOpen] = useState(false);

  const [wantedAdjustmentToolMode, setWantedAdjustmentToolMode] =
    useState<WantedAdjustmentToolMode>("assignment");
  const [isWantedAdjustmentToolOpen, setIsWantedAdjustmentToolOpen] =
    useState(false);
  const [activeFloatingPanel, setActiveFloatingPanel] = useState<
    "memo" | "shift"
  >("shift");
  const assignmentTriggerRef = useRef<HTMLButtonElement>(null);
  const isAssignmentPanelOpen =
    isWantedAdjustmentToolOpen && wantedAdjustmentToolMode === "assignment";

  // 원티드 전체 반영/미반영 토글 상태
  const [isAllApplied, setIsAllApplied] = useState(true);

  // 조정판 전용 위반사항 표시 상태 (로컬 상태, 전역 상태와 분리)
  const [localShowManpowerViolations, setLocalShowManpowerViolations] =
    useState(true);
  const [localShowConfigViolations, setLocalShowConfigViolations] =
    useState(true);
  const [localShowWantedViolations, setLocalShowWantedViolations] =
    useState(false); // 조정판에서는 원티드 반영사항 보기 불필요

  // 선택된 근무코드 상태
  const [selectedShift, setSelectedShift] =
    useState<type.Type_availableShifts | null>(null);
  const [selectedBannedShifts, setSelectedBannedShifts] = useState<
    type.Type_availableShifts[]
  >([]);
  const [isClearingWantedShift, setIsClearingWantedShift] = useState(false);
  const [isClearingBannedShift, setIsClearingBannedShift] = useState(false);
  const [modifiedBannedEntries, setModifiedBannedEntries] = useState<
    Map<string, BannedWantedAdjustmentEntry | null>
  >(new Map());

  const handleWantedAdjustmentToolModeChange = useCallback(
    (mode: WantedAdjustmentToolMode) => {
      setWantedAdjustmentToolMode(mode);
    },
    []
  );

  // wanted/adjustment API 호출 (스냅샷 데이터)
  const {
    data: wantedAdjustmentData,
    isLoading: isWantedAdjustmentLoading,
    refetch: refetchWantedAdjustment,
  } = useWantedAdjustmentQuery(
    currentScheduleInfo.year,
    currentScheduleInfo.month
  );

  const wantedAssignmentsByNurse = useMemo(() => {
    const map = new Map<string, InboundItem[]>();
    const rows = wantedAdjustmentData?.assignments ?? [];
    for (const a of rows) {
      const nurseId = String(a.nurse_id ?? "").trim();
      if (nurseId === "") continue;
      const startDate = a.start_date ?? a.startDate ?? "";
      if (startDate === "") continue;
      const prev = map.get(nurseId) ?? [];
      prev.push({
        startDate,
        endDate: a.end_date ?? a.endDate ?? null,
        reason: a.reason,
        target_group_id: a.target_group_id ?? a.targetGroupId,
        target_group_name: a.target_group_name ?? a.targetGroupName,
      });
      map.set(nurseId, prev);
    }
    return map;
  }, [wantedAdjustmentData?.assignments]);

  // 초기 데이터 저장 (deep copy)
  useEffect(() => {
    if (wantedAdjustmentData && !initialWantedAdjustmentData) {
      const deepCopy = JSON.parse(
        JSON.stringify(wantedAdjustmentData)
      ) as WantedAdjustmentData;
      setInitialWantedAdjustmentData(deepCopy);
    }
  }, [wantedAdjustmentData, initialWantedAdjustmentData]);

  useEffect(() => {
    setShowPerShiftStats(false);
  }, [currentScheduleInfo.year, currentScheduleInfo.month, currentScheduleID]);

  // wantedAdjustmentData를 copyNurses 형태로 변환 (원본 순서·내용)
  const wantedCopyNursesComputed = useMemo(() => {
    if (wantedBoardMonthlyNurses.length === 0) {
      return [];
    }

    // active === 1인 인원만 필터링
    const activeNurses = wantedBoardMonthlyNurses.filter(
      (nurse) => nurse.active === 1
    );
    if (activeNurses.length === 0) {
      return [];
    }

    // 로딩 중이거나 wantedAdjustmentData가 없으면 빈 스케줄로 표시 (간호사 목록은 먼저 보여줌)
    if (isWantedAdjustmentLoading || !wantedAdjustmentData?.nurses) {
      const daysInMonth = new Date(
        currentScheduleInfo.year,
        currentScheduleInfo.month,
        0
      ).getDate();

      const emptyCounts: Record<string, number> = {};
      defaultShiftCode.forEach((s) => {
        if (s.shift_id) emptyCounts[s.shift_id] = 0;
      });

      return sortNurseListByRoleThenSequence(
        activeNurses.map((nurse) => ({
          ...nurse,
          id: nurse.nurse_id, // id 필드 추가
          off: nurse.personal_off_adjustment ?? 0, // off 필드 추가
          level: nurse.level_ ?? null, // level 필드 추가
          schedule: Array(daysInMonth).fill(null),
          schedule_ids: Array(daysInMonth).fill(null),
          counts: emptyCounts,
          shiftMessages: {},
        }))
      );
    }

    const daysInMonth = new Date(
      currentScheduleInfo.year,
      currentScheduleInfo.month,
      0
    ).getDate();

    // wantedAdjustmentData.nurses를 Map으로 변환 (빠른 조회를 위해)
    const wantedNursesMap = new Map(
      wantedAdjustmentData.nurses.map((wantedNurse) => [
        wantedNurse.nurse_id,
        wantedNurse,
      ])
    );

    const rows = activeNurses.map((nurse) => {
      const wantedNurse: WantedAdjustmentNurse | undefined =
        wantedNursesMap.get(nurse.nurse_id);

      // schedule 배열 초기화 (원티드 데이터만 표시)
      const schedule: (string | null)[] = Array(daysInMonth).fill(null);
      const scheduleIds: (number | null)[] = Array(daysInMonth).fill(null);
      const shiftMessages: Record<number, string> = {};
      const isAppliedMap: Record<number, boolean> = {}; // 날짜별 is_applied 정보
      const bannedShiftMap: Record<
        number,
        {
          shiftIds: string[];
          isApplied: boolean;
          source?: string;
          reason?: string | null;
        }
      > = {};

      if (wantedNurse?.entries) {
        // entries를 파싱해서 schedule 배열 생성
        wantedNurse.entries.forEach((entry: WantedAdjustmentEntry) => {
          if (entry.shift_date && entry.shift_id) {
            // shift_date에서 날짜 추출 (예: "2026-03-15" -> 15)
            const date = new Date(entry.shift_date);
            const day = date.getDate();

            // 유효한 날짜인지 확인 (1 ~ daysInMonth)
            if (day >= 1 && day <= daysInMonth) {
              const dayIndex = day - 1; // 0-based index

              // modifiedEntries에서 변경된 entry 확인
              const entryKey = `${nurse.nurse_id}_${entry.shift_date}`;
              const modifiedEntry = modifiedEntries.get(entryKey);
              const currentEntry = modifiedEntries.has(entryKey)
                ? modifiedEntry
                : entry;
              if (!currentEntry) return;
              const isApplied = currentEntry.is_applied;

              const tableId =
                currentEntry.shifts_table_id != null &&
                Number.isFinite(Number(currentEntry.shifts_table_id))
                  ? Number(currentEntry.shifts_table_id)
                  : null;

              // is_applied 정보 저장 (X 아이콘 표시용)
              isAppliedMap[day] = isApplied;

              // is_applied가 false여도 표시 (X 아이콘으로 표시)
              // isViewOnlyOff 모드면 O만 표시, 아니면 모든 shift 표시
              if (isViewOnlyOff) {
                if (currentEntry.shift_id === "O") {
                  schedule[dayIndex] = "O";
                  scheduleIds[dayIndex] = tableId;
                }
              } else {
                schedule[dayIndex] = currentEntry.shift_id;
                scheduleIds[dayIndex] = tableId;
              }

              // reason이 있으면 shiftMessages에 추가
              if (currentEntry.reason) {
                shiftMessages[day] = currentEntry.reason;
              }
            }
          }
        });
      }

      wantedNurse?.banned_entries?.forEach((entry) => {
        const entryKey = `${nurse.nurse_id}_${entry.shift_date}`;
        const modifiedEntry = modifiedBannedEntries.get(entryKey);
        const currentEntry = modifiedEntry === undefined ? entry : modifiedEntry;
        if (!currentEntry) return;
        const day = new Date(currentEntry.shift_date).getDate();
        if (day >= 1 && day <= daysInMonth) {
          bannedShiftMap[day] = {
            shiftIds: currentEntry.banned_shift_ids,
            isApplied: currentEntry.is_applied,
            source: currentEntry.source,
            reason: currentEntry.reason,
          };
        }
      });

      modifiedBannedEntries.forEach((entry, entryKey) => {
        const [modifiedNurseId] = entryKey.split("_");
        if (modifiedNurseId !== nurse.nurse_id || !entry) return;
        const day = new Date(entry.shift_date).getDate();
        if (day >= 1 && day <= daysInMonth) {
          bannedShiftMap[day] = {
            shiftIds: entry.banned_shift_ids,
            isApplied: entry.is_applied,
            source: entry.source,
            reason: entry.reason,
          };
        }
      });

      // // counts 계산: schedule 기준으로 is_applied=true인 항목만 집계
      // // isAppliedMap을 확인하여 실시간으로 재계산
      // const counts = {
      //   D: 0,
      //   E: 0,
      //   N: 0,
      //   O: 0,
      // };

      // // schedule을 순회하면서 is_applied=true인 항목만 집계
      // schedule.forEach((shift, dayIndex) => {
      //   if (shift) {
      //     const day = dayIndex + 1; // 1-based
      //     const isApplied = isAppliedMap[day] ?? true; // 기본값은 true

      //     // is_applied=true인 항목만 집계
      //     if (isApplied) {
      //       if (shift === "D") counts.D++;
      //       else if (shift === "E") counts.E++;
      //       else if (shift === "N") counts.N++;
      //       else if (shift === "O") counts.O++;
      //     }
      //   }
      // });

      // 2. modifiedEntries에만 있는 새로운 항목 처리
      modifiedEntries.forEach((modifiedEntry, entryKey) => {
        const [modifiedNurseId] = entryKey.split("_");

        if (modifiedNurseId === nurse.nurse_id && modifiedEntry) {
          const date = new Date(modifiedEntry.shift_date);
          const day = date.getDate();

          if (day >= 1 && day <= daysInMonth) {
            const dayIndex = day - 1;

            const tableId =
              modifiedEntry.shifts_table_id != null &&
              Number.isFinite(Number(modifiedEntry.shifts_table_id))
                ? Number(modifiedEntry.shifts_table_id)
                : null;

            // schedule 업데이트
            if (isViewOnlyOff) {
              if (modifiedEntry.shift_id === "O") {
                schedule[dayIndex] = "O";
                scheduleIds[dayIndex] = tableId;
              }
            } else {
              schedule[dayIndex] = modifiedEntry.shift_id;
              scheduleIds[dayIndex] = tableId;
            }

            // is_applied 정보 저장
            isAppliedMap[day] = modifiedEntry.is_applied;

            // reason이 있으면 추가
            if (modifiedEntry.reason) {
              shiftMessages[day] = modifiedEntry.reason;
            }
          }
        }
      });

      // counts 계산: schedule 기준으로 is_applied=true인 항목만 집계
      // isAppliedMap을 확인하여 실시간으로 재계산
      const counts: Record<string, number> = {};
      defaultShiftCode.forEach((s) => {
        if (s.shift_id) counts[s.shift_id] = 0;
      });

      // schedule을 순회하면서 is_applied=true인 항목만 집계
      schedule.forEach((shift, dayIndex) => {
        if (shift) {
          const day = dayIndex + 1; // 1-based
          const isApplied = isAppliedMap[day] ?? true; // 기본값은 true

          // is_applied=true인 항목만 집계
          if (isApplied) {
            const countKey =
              resolveShiftFromWantedEntry(
                {
                  shift_id: shift,
                  shifts_table_id: scheduleIds[dayIndex],
                },
                shiftLookupMaps
              )?.shift_id ?? shift;
            counts[countKey] = (counts[countKey] || 0) + 1;
          }
        }
      });

      return {
        ...nurse,
        id: nurse.nurse_id, // id 필드 추가 (row_NurseRosterList에서 사용)
        off: nurse.personal_off_adjustment ?? 0, // off 필드 추가
        level: nurse.level_ ?? null, // level 필드 추가
        schedule,
        schedule_ids: scheduleIds,
        counts,
        shiftMessages,
        // 원티드 반영 설정용: entries 정보 추가
        wantedEntries: wantedNurse?.entries || [],
        // 원티드 반영 설정용: 날짜별 is_applied 정보 (X 아이콘 표시용)
        isAppliedMap: isAppliedMap,
        bannedShiftMap: withoutBannedShiftsOnAppliedFixedDays(
          bannedShiftMap,
          isAppliedMap
        ),
        // 원티드 요청 API의 assignment 구간(파견/휴직 등) — 조정판 잠금/툴팁 기준
        assignments:
          wantedAssignmentsByNurse.get(String(nurse.nurse_id).trim()) ??
          // wanted 쿼리 비어있는 경우 기존 roster nurse의 배정 정보 fallback
          (nurse as unknown as { assignments?: InboundItem[] }).assignments ??
          [],
      };
    });
    return sortNurseListByRoleThenSequence(rows);
  }, [
    wantedBoardMonthlyNurses,
    wantedAdjustmentData,
    currentScheduleInfo.year,
    currentScheduleInfo.month,
    isViewOnlyOff,
    modifiedEntries,
    modifiedBannedEntries,
    defaultShiftCode,
    isWantedAdjustmentLoading, // 로딩 상태 추가
    shiftLookupMaps,
    wantedAssignmentsByNurse,
  ]);

  const [wantedCopyNurses, setWantedCopyNurses] = useState(
    wantedCopyNursesComputed
  );

  useEffect(() => {
    setWantedCopyNurses(wantedCopyNursesComputed);
  }, [wantedCopyNursesComputed]);

  const displayedWantedCopyNurses = wantedCopyNurses;

  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;
  const wantedBoardLayoutConfig = useMemo(
    () =>
      buildRosterTableLayoutConfig({
        showPreceptorColumn: Boolean(serverConfig?.show_preceptor),
        showLevelColumn: Boolean(serverConfig?.show_level),
        showRowReorder: true,
      }),
    [serverConfig?.show_level, serverConfig?.show_preceptor]
  );

  const {
    moveRosterNurseRow: moveWantedBoardNurseRow,
    sortRosterNurseRows: sortWantedBoardNurseRows,
  } = useRosterCreateNurseReorder({
    copyNurses: wantedCopyNurses,
    setCopyNurses: setWantedCopyNurses,
    sequenceSourceRows: nurses ?? [],
    groupId: wantedBoardGroupId,
  });

  // 검증용 copyNurses: 프리셉티 제외 + isAppliedMap=false(거절된 원티드) 투명화
  const wantedPrecepteePolicy = getPrecepteePolicy(serverConfig);
  const validationCopyNurses = useMemo(() => {
    return (
      wantedCopyNurses
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((nurse: any) =>
          wantedPrecepteePolicy.excludePrecepteeFromCount
            ? nurse.preceptor?.role !== "preceptee"
            : true
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((nurse: any) => {
          if (!nurse.isAppliedMap) return nurse;
          return {
            ...nurse,
            schedule:
              nurse.schedule?.map((shift: string | null, idx: number) => {
                const day = idx + 1;
                if (nurse.isAppliedMap[day] === false) return null;
                return shift;
              }) ?? null,
          };
        })
    );
  }, [wantedCopyNurses, wantedPrecepteePolicy.excludePrecepteeFromCount]);

  // RosterMadeTable 통계용 전처리: 프리셉티 제외 + M 필터
  const wantedStatsNurses = useMemo(
    () =>
      wantedPrecepteePolicy.excludePrecepteeFromCount
        ? wantedCopyNurses.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (n: any) => n.preceptor?.role !== "preceptee"
          )
        : wantedCopyNurses,
    [wantedCopyNurses, wantedPrecepteePolicy.excludePrecepteeFromCount]
  );
  const wantedStatsShiftCode = useMemo(
    () =>
      isUseMid === false
        ? defaultShiftCode.filter((s) => s.default_shift !== "M")
        : defaultShiftCode,
    [defaultShiftCode, isUseMid]
  );

  // 원티드 조정판 전용 검증 (최소인원 검증 비활성화)
  const {
    validateList: rawWantedValidateList,
    isValidating: isWantedValidating,
    phase: wantedValidationPhase,
    validate: triggerWantedValidation,
  } = useRosterValidation({
    currentScheduleInfo,
    currentScheduleID,
    copyNurses: validationCopyNurses as Parameters<
      typeof useRosterValidation
    >[0]["copyNurses"],
    isDragEditing,
    manPower_monthly: isWantedAdjustmentLoading ? null : manPower_monthly,
    year: currentScheduleInfo.year,
    month: currentScheduleInfo.month,
    serverConfig: serverConfig ?? null,
    defaultShiftCode,
    shiftsData: shiftsData ?? [],
    disableManpowerValidation: true,
    prevTailData,
    memberStatusMap: wantedBoardMemberStatusMap,
  });

  useEffect(() => {
    if (!isDragEditing && !isWantedAdjustmentLoading) triggerWantedValidation();
  }, [
    validationCopyNurses,
    currentScheduleID,
    isDragEditing,
    isWantedAdjustmentLoading,
    triggerWantedValidation,
  ]);

  const { displayShifts: wantedModalShiftLegend } =
    useRosterDisplayShiftColumns(
      wantedCopyNurses as {
        schedule?: (string | null)[] | null;
        schedule_ids?: (number | null)[] | null;
      }[],
      defaultShiftCode,
      isUseMid,
      { includeBaselineMainTemplateColumns: true }
    );

  // 원티드 반영 설정: 셀 클릭 핸들러 (is_applied 토글)
  const handleWantedCellClick = useCallback(
    (nurseId: string, day: number, shiftId?: string) => {
      const daysInMonth = new Date(
        currentScheduleInfo.year,
        currentScheduleInfo.month,
        0
      ).getDate();

      if (day < 1 || day > daysInMonth) {
        return;
      }

      // 해당 간호사의 wantedNurse 찾기
      const wantedNurse = wantedAdjustmentData?.nurses.find(
        (n) => n.nurse_id === nurseId
      );

      // 날짜 형식: YYYY-MM-DD
      const year = currentScheduleInfo.year;
      const month = String(currentScheduleInfo.month).padStart(2, "0");
      const dayStr = String(day).padStart(2, "0");
      const dateStr = `${year}-${month}-${dayStr}`;

      // entryKey 생성
      const entryKey = `${nurseId}_${dateStr}`;

      const isBannedToolActive =
        isWantedAdjustmentToolOpen && wantedAdjustmentToolMode === "banned";
      const isAssignmentToolActive =
        isWantedAdjustmentToolOpen && wantedAdjustmentToolMode === "assignment";
      const isClearToolActive =
        (isBannedToolActive && isClearingBannedShift) ||
        (isAssignmentToolActive && isClearingWantedShift);

      if ((isBannedToolActive || isAssignmentToolActive) && shiftId === "주") {
        return;
      }

      if (isClearToolActive) {
        setModifiedEntries((previous) => {
          const next = new Map(previous);
          next.set(entryKey, null);
          return next;
        });
        setModifiedBannedEntries((previous) => {
          const next = new Map(previous);
          next.set(entryKey, null);
          return next;
        });
        return;
      }

      if (isBannedToolActive && selectedBannedShifts.length > 0) {
        const existingFixedEntry = wantedNurse?.entries.find(
          (entry) => entry.shift_date === dateStr
        );
        const effectiveFixedEntry = modifiedEntries.has(entryKey)
          ? modifiedEntries.get(entryKey)
          : existingFixedEntry;

        if (effectiveFixedEntry?.is_applied) {
          toast.info("반영 중인 고정 근무가 있어 금지 근무를 설정할 수 없습니다.");
          return;
        }

        const existingBannedEntry = wantedNurse?.banned_entries?.find(
          (entry) => entry.shift_date === dateStr
        );
        const modifiedBannedEntry = modifiedBannedEntries.get(entryKey);
        const effectiveBannedEntry =
          modifiedBannedEntry === undefined
            ? existingBannedEntry
            : modifiedBannedEntry;
        const nextShiftIds = selectedBannedShifts.map((shift) => shift.shift_id);

        setModifiedEntries((previous) => {
          const next = new Map(previous);
          next.set(entryKey, null);
          return next;
        });

        setModifiedBannedEntries((previous) => {
          const next = new Map(previous);
          if (nextShiftIds.length === 0) {
            next.set(entryKey, null);
            return next;
          }
          next.set(entryKey, {
            id: effectiveBannedEntry?.id ?? Date.now(),
            group_id: effectiveBannedEntry?.group_id ?? selectedGroupId ?? "",
            year,
            month: currentScheduleInfo.month,
            nurse_id: nurseId,
            shift_date: dateStr,
            banned_shift_ids: [...nextShiftIds],
            is_applied: effectiveBannedEntry?.is_applied ?? true,
            reason: effectiveBannedEntry?.reason ?? null,
            created_by: effectiveBannedEntry?.created_by ?? null,
          });
          return next;
        });
        return;
      }

      // 근무코드가 선택된 경우: 근무코드 추가/변경 모드
      if (isAssignmentToolActive && selectedShift) {
        const existingEntry = wantedNurse?.entries.find(
          (e) => e.shift_date === dateStr
        );

        const pickedShiftRow = shiftInfo.find(
          (s) => s.shift_id === selectedShift.shift_id
        );
        const shiftsTableIdFromPicker =
          pickedShiftRow?.id != null &&
          Number.isFinite(Number(pickedShiftRow.id))
            ? Number(pickedShiftRow.id)
            : 0;

        const newEntry: WantedAdjustmentEntry = existingEntry
          ? {
              ...existingEntry,
              shift_id: selectedShift.shift_id,
              shifts_table_id:
                shiftsTableIdFromPicker || existingEntry.shifts_table_id,
              is_applied: true,
            }
          : {
              id: Date.now(),
              group_id: selectedGroupId || "",
              year: currentScheduleInfo.year,
              month: currentScheduleInfo.month,
              nurse_id: nurseId,
              shift_date: dateStr,
              shift_id: selectedShift.shift_id,
              shifts_table_id: shiftsTableIdFromPicker,
              is_applied: true,
              source_type: "manual",
              original_shift_id: null,
              reason: null,
              head_nurse_memo: null,
              created_by: null,
            };

        setModifiedBannedEntries((previous) => {
          const next = new Map(previous);
          next.set(entryKey, null);
          return next;
        });

        setModifiedEntries((prev) => {
          const newMap = new Map(prev);
          newMap.set(entryKey, newEntry);
          return newMap;
        });
        return;
      }

      const existingBannedEntry = wantedNurse?.banned_entries?.find(
        (entry) => entry.shift_date === dateStr
      );
      const modifiedBannedEntry = modifiedBannedEntries.get(entryKey);
      const effectiveBannedEntry =
        modifiedBannedEntry === undefined
          ? existingBannedEntry
          : modifiedBannedEntry;

      if (effectiveBannedEntry) {
        setModifiedBannedEntries((previous) => {
          const next = new Map(previous);
          next.set(entryKey, toggleBannedEntryApplied(effectiveBannedEntry));
          return next;
        });
        return;
      }

      // 주휴: 반영(is_applied) 토글 불가 (스케줄 칸 코드 기준)
      if (shiftId === "주") {
        return;
      }

      // 🆕 is_applied 토글 모드
      // 1. modifiedEntries에 있는지 먼저 확인
      const modifiedEntry = modifiedEntries.get(entryKey);

      if (modifiedEntry) {
        if (modifiedEntry.shift_id === "주") {
          return;
        }
        // modifiedEntries에 있으면 그것을 토글
        const newEntry: WantedAdjustmentEntry = {
          ...modifiedEntry,
          is_applied: !modifiedEntry.is_applied,
        };

        setModifiedEntries((prev) => {
          const newMap = new Map(prev);
          newMap.set(entryKey, newEntry);
          return newMap;
        });
        return;
      }

      if (!wantedNurse) {
        return;
      }

      // shiftId가 있으면 shift_id도 매칭, 없으면 날짜만 매칭
      const entry = wantedNurse.entries.find((e) => {
        const dateMatch = e.shift_date === dateStr;
        if (shiftId) {
          return dateMatch && e.shift_id === shiftId;
        }
        return dateMatch;
      });

      if (!entry) {
        return; // 해당 날짜에 원티드가 없으면 무시
      }

      if (entry.shift_id === "주") {
        return;
      }

      // modifiedEntries에서 현재 상태 확인
      const currentEntry = modifiedEntries.has(entryKey)
        ? modifiedEntries.get(entryKey)
        : entry;
      if (!currentEntry) return;

      // is_applied 토글
      const newEntry: WantedAdjustmentEntry = {
        ...currentEntry,
        is_applied: !currentEntry.is_applied,
      };

      // modifiedEntries 업데이트
      setModifiedEntries((prev) => {
        const newMap = new Map(prev);
        newMap.set(entryKey, newEntry);
        return newMap;
      });
    },
    [
      currentScheduleInfo.year,
      currentScheduleInfo.month,
      wantedAdjustmentData,
      modifiedEntries,
      selectedShift,
      selectedBannedShifts,
      isWantedAdjustmentToolOpen,
      wantedAdjustmentToolMode,
      isClearingWantedShift,
      isClearingBannedShift,
      modifiedBannedEntries,
      selectedGroupId,
      shiftInfo,
    ]
  );

  // 원티드 전체 반영/미반영 토글 핸들러
  const handleToggleAllApplied = useCallback(() => {
    const newAppliedState = !isAllApplied;
    const newModifiedEntries = new Map(modifiedEntries);

    // 1. wantedAdjustmentData에 있는 entries 처리
    if (wantedAdjustmentData?.nurses) {
      wantedAdjustmentData.nurses.forEach((nurse) => {
        nurse.entries.forEach((entry) => {
          // 주휴 코드 제외
          if (entry.shift_id === "주") return;

          const entryKey = `${nurse.nurse_id}_${entry.shift_date}`;
          const currentEntry = modifiedEntries.has(entryKey)
            ? modifiedEntries.get(entryKey)
            : entry;
          if (!currentEntry) return;

          newModifiedEntries.set(entryKey, {
            ...currentEntry,
            is_applied: newAppliedState,
          });
        });
      });
    }

    // 2. modifiedEntries에만 있는 새로운 항목도 처리
    modifiedEntries.forEach((entry, entryKey) => {
      if (!entry) return;
      // 주휴 코드 제외
      if (entry.shift_id === "주") return;

      newModifiedEntries.set(entryKey, {
        ...entry,
        is_applied: newAppliedState,
      });
    });

    setModifiedEntries(newModifiedEntries);
    setIsAllApplied(newAppliedState);

    toast.success(
      newAppliedState
        ? "모든 원티드가 반영되었습니다."
        : "모든 원티드가 미반영되었습니다."
    );
  }, [wantedAdjustmentData, modifiedEntries, isAllApplied]);

  const getWantedEntriesForSave = useCallback(() => {
    const entries = wantedAdjustmentData?.nurses.flatMap(
      (nurse) => nurse.entries
    );
    return mergeWantedEntryOverrides(entries ?? [], modifiedEntries);
  }, [modifiedEntries, wantedAdjustmentData]);

  const getBannedEntriesForSave = useCallback(() => {
    const entriesByKey = new Map<string, BannedWantedAdjustmentEntry>();
    wantedAdjustmentData?.nurses.forEach((nurse) => {
      nurse.banned_entries?.forEach((entry) => {
        entriesByKey.set(`${entry.nurse_id}_${entry.shift_date}`, entry);
      });
    });
    modifiedBannedEntries.forEach((entry, key) => {
      if (entry) entriesByKey.set(key, entry);
      else entriesByKey.delete(key);
    });
    return Array.from(entriesByKey.values()).map((entry) => ({
      nurse_id: entry.nurse_id,
      shift_date: entry.shift_date,
      banned_shift_ids: entry.banned_shift_ids,
      is_applied: entry.is_applied,
      reason: entry.reason,
    }));
  }, [modifiedBannedEntries, wantedAdjustmentData]);

  // 리셋로직
  const {
    mutate: resetWantedAdjustment,
    isPending: isResettingWantedAdjustment,
  } = useMutation({
    mutationFn: async () => {
      if (!currentScheduleInfo.year || !currentScheduleInfo.month) {
        throw new Error("필수 항목이 누락 되었습니다.");
      }

      const response = await httpClient.post<WantedAdjustmentData>(
        `/wanted/adjustment/${currentScheduleInfo.year}/${currentScheduleInfo.month}/reset`
      );
      return response;
    },
    onSuccess: (data) => {
      // mutation 리턴값으로 쿼리 캐시 업데이트
      queryClient.setQueryData(
        QUERY_KEYS.wanted.adjustment(
          currentScheduleInfo.year,
          currentScheduleInfo.month
        ),
        data
      );

      // 초기 데이터도 업데이트
      const deepCopy = JSON.parse(JSON.stringify(data)) as WantedAdjustmentData;
      setInitialWantedAdjustmentData(deepCopy);

      // modifiedEntries 초기화
      setModifiedEntries(new Map());
      setModifiedBannedEntries(new Map());
      setIsAllApplied(true);

      toast.success("원티드 반영 설정이 초기화 되었습니다.");
    },
    onError: (error) => {
      console.error("원티드 반영 설정 초기화 실패:", error);
      toast.error("원티드 반영 설정 초기화에 실패했습니다.");
    },
  });

  // 초기화 핸들러
  const handleReset = useCallback(() => {
    if (!confirm("초기화 하시겠습니까?")) {
      return;
    }
    resetWantedAdjustment();
  }, [resetWantedAdjustment]);

  // 닫기 핸들러: 변경사항이 있으면 확인 모달 표시
  const handleClose = useCallback(() => {
    // 진행 중인 wanted/adjustment 쿼리 취소
    if (currentScheduleInfo?.year && currentScheduleInfo?.month) {
      queryClient.cancelQueries({
        queryKey: QUERY_KEYS.wanted.adjustment(
          currentScheduleInfo.year,
          currentScheduleInfo.month
        ),
      });
    }

    // 변경사항이 있으면 확인 모달 표시
    if (modifiedEntries.size > 0 || modifiedBannedEntries.size > 0) {
      setIsCloseConfirmModalOpen(true);
    } else {
      // 변경사항이 없으면 바로 닫기
      onClose();
    }
  }, [
    modifiedEntries.size,
    modifiedBannedEntries.size,
    onClose,
    queryClient,
    currentScheduleInfo?.year,
    currentScheduleInfo?.month,
  ]);

  const {
    mutate: updateWantedAdjustment,
    isPending: isUpdatingWantedAdjustment,
  } = useMutation({
    mutationFn: async () => {
      if (!wantedAdjustmentData) {
        throw new Error("원티드 데이터가 없습니다.");
      }

      const entries: Array<{
        nurse_id: string;
        shift_date: string;
        shift_id: string;
        shifts_table_id: number;
        is_applied: boolean;
        reason: string | null;
        head_nurse_memo: string | null;
      }> = [];

      getWantedEntriesForSave().forEach((entry) => {
        entries.push({
          nurse_id: entry.nurse_id,
          shift_date: entry.shift_date,
          shift_id: entry.shift_id,
          shifts_table_id: entry.shifts_table_id,
          is_applied: entry.is_applied,
          reason: entry.reason,
          head_nurse_memo: entry.head_nurse_memo,
        });
      });

      const postData = {
        year: currentScheduleInfo.year,
        month: currentScheduleInfo.month,
        entries: entries,
        banned_entries: getBannedEntriesForSave(),
      };

      // throw new Error("error")
      return await httpClient.post<WantedAdjustmentSaveResponse>(
        "/wanted/adjustment",
        postData
      );
    },
    onSuccess: async (data) => {
      const refreshResult = await refetchWantedAdjustment();
      if (refreshResult.isError) {
        toast.error(
          "원티드 설정은 저장됐지만 최신 상태를 불러오지 못했습니다. 다시 시도해주세요."
        );
        return;
      }

      toast.success("원티드 반영 설정이 저장되었습니다.");
      // 저장 후 modifiedEntries 초기화
      setModifiedEntries(new Map());
      setModifiedBannedEntries(new Map());
      const warnings = data.warnings;
      if (warnings?.some((warning) => warning.reason === "dropped_on_fixed_cell")) {
        toast.info("확정 근무가 있는 셀의 금지 설정은 저장되지 않았습니다.");
      }
      // 간호사 본인이 낸 기피근무는 근무코드를 바꿀 수 없다(적용 여부만).
      // 안 알리면 바뀐 줄 알고 넘어간다.
      if (
        warnings?.some(
          (warning) => warning.reason === "nurse_owned_codes_readonly"
        )
      ) {
        toast.info(
          "간호사가 요청한 기피 근무는 근무코드를 바꿀 수 없어 적용 여부만 반영했습니다."
        );
      }
    },
    onError: (error) => {
      console.error("원티드 반영 설정 저장 실패:", error);
      // 서버가 사전 점검으로 막은 경우 사유가 메시지에 담겨 온다(예: 나이트 전담이
      // 연달아 비어 근무표를 만들 수 없는 조합). 일반 문구로 덮으면 사용자는
      // 무엇을 고쳐야 하는지 알 수 없다.
      const detail =
        error instanceof Error && error.message ? error.message : "";
      toast.error(detail || "원티드 반영 설정 저장에 실패했습니다.");
    },
  });

  const wantedLoadingOverlayMessage = isWantedAdjustmentLoading
    ? "전체 근무자 원티드 현황을 가져오는 중입니다."
    : isUpdatingWantedAdjustment
      ? "원티드 설정을 저장하고 최신 상태를 불러오는 중입니다."
      : isResettingWantedAdjustment
        ? "원티드 반영 설정을 초기화하는 중입니다."
        : null;

  /** 모달이 `stopPropagation`으로 버블을 끊어 `TableBody_base`의 window 캡처와 겹치지 않는 경우 대비 */
  const clearRosterTableCellFocusIfOutside = useCallback((e: MouseEvent) => {
    const t = e.target as HTMLElement | null;
    if (
      t &&
      !t.closest("[data-roster-shift-cell]") &&
      !t.closest("[data-roster-header-day]") &&
      !t.closest("[data-monthly-memo-post-it]") &&
      !useRosterFocusStore.getState().isSelectedFocusPinned
    ) {
      useRosterFocusStore.getState().clearSelectedFocus();
    }
  }, []);

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        hideCloseButton
        data-wanted-adjustment-focus-scope
        className={cn(
          "us-n !inset-0 !h-auto !w-auto !max-w-none !translate-x-0 !translate-y-0 !gap-0 !overflow-visible border-0 bg-transparent p-0 shadow-none pointer-events-none"
        )}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          event.currentTarget.focus();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          data-wanted-adjustment-dialog
          className={cn(
            "pointer-events-auto absolute left-1/2 top-1/2 flex !h-fit !max-h-[calc(100dvh-40px)] !w-fit !max-w-[calc(100vw-40px)] min-h-0 -translate-x-1/2 -translate-y-1/2 flex-1 flex-col gap-0 overflow-hidden rounded-[10px] border-0 bg-white p-0 font-pretendard shadow-lg",
            wantedLoadingOverlayMessage && "overflow-hidden"
          )}
          onClick={(e) => {
            clearRosterTableCellFocusIfOutside(e);
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            clearRosterTableCellFocusIfOutside(e);
            e.stopPropagation();
          }}
        >
          {wantedLoadingOverlayMessage && (
            <LoadingOverlay message={wantedLoadingOverlayMessage} />
          )}
          <DialogTitle className="sr-only">원티드 관리보드</DialogTitle>
          <DialogHeader className="z-20 shrink-0 space-y-0 border-b bg-white px-5 pt-1">
            <div className="flex w-full flex-wrap items-end justify-between gap-x-4 gap-y-2">
              <div className="min-w-[280px] pb-2 pt-5">
                <h2 className="text-base font-semibold text-zinc-800">
                  원티드 관리보드 설정
                </h2>
                <p className="mt-0.5 text-xs font-medium text-zinc-500">
                  전체 근무자 원티드 요청 현황
                </p>
              </div>

              <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 pb-2 whitespace-nowrap">
                <MonthlyMemoPostIt
                  year={currentScheduleInfo.year}
                  month={currentScheduleInfo.month}
                  groupId={wantedBoardGroupId}
                  memos={Array.from(monthlyMemoByNurse.values())}
                  layerZIndex={activeFloatingPanel === "memo" ? 130 : 70}
                  onActivate={() => setActiveFloatingPanel("memo")}
                  onClose={() => {
                    const focus = useRosterFocusStore.getState();
                    if (focus.isSelectedFocusPinned) {
                      focus.clearSelectedFocus();
                    }
                  }}
                  onSelectNurse={(nurseId) => {
                    if (!nurseId) {
                      useRosterFocusStore.getState().clearSelectedFocus();
                      return;
                    }

                    useRosterFocusStore.getState().setSelectedFocus({
                      column: "",
                      nurseId,
                      retainOnOutsideClick: true,
                    });
                    const nurseRow = Array.from(
                      document.querySelectorAll<HTMLElement>(
                        "[data-nurse-row-id]"
                      )
                    ).find((element) => element.dataset.nurseRowId === nurseId);
                    nurseRow?.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                      inline: "nearest",
                    });
                  }}
                />
                <ActionButton
                  ref={assignmentTriggerRef}
                  label="+ 근무코드 배정"
                  ariaLabel={
                    isAssignmentPanelOpen
                      ? "근무코드 배정 닫기"
                      : "근무코드 배정 열기"
                  }
                  ariaPressed={isAssignmentPanelOpen}
                  className={
                    isAssignmentPanelOpen
                      ? "border-sig bg-sig/5 text-sig hover:border-sig hover:text-sig"
                      : undefined
                  }
                  disabled={isWantedAdjustmentLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isAssignmentPanelOpen) {
                      setIsWantedAdjustmentToolOpen(false);
                      return;
                    }

                    handleWantedAdjustmentToolModeChange("assignment");
                    setActiveFloatingPanel("shift");
                    setIsWantedAdjustmentToolOpen(true);
                  }}
                />
                <ActionButton
                  label={isAllApplied ? "원티드 전체 미반영" : "원티드 전체 반영"}
                  disabled={isWantedAdjustmentLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isAllApplied) {
                      handleToggleAllApplied();
                    } else {
                      handleReset();
                    }
                  }}
                />
                <ActionButton
                  label="저장"
                  disabled={isWantedAdjustmentLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsConfirmModalOpen(true);
                  }}
                />
                <ActionButton
                  label="닫기"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  ariaLabel="close"
                />
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-auto scrollbar-float">
            {/* 모달 컨텐츠 — 상단 헤더/통계 그룹은 Roster_create.tsx (989–1161)와 동일 DOM·클래스 구조 */}
            <div className="bg-white flex flex-col pt-0 pb-5">
              <div className={cn("sticky top-0 z-20 bg-white px-5")}>
                <div className={cn("pt-5 bg-white mx-auto w-max")}>
                  <div className=" flex flex-col">
                    <section
                      className="mt-0 mb-4 w-full flex justify-between items-center rounded-lg border border-zinc-200 bg-[#FAF9F9] px-4 py-3"
                      aria-label="보기 설정"
                    >
                      <h2 className="text-sm font-semibold text-zinc-700">
                        보기 설정
                      </h2>
                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-6 sm:gap-y-3">
                        <WantedSettingSwitchRow
                          label="설정 위반사항 보기"
                          checked={localShowConfigViolations}
                          onCheckedChange={(checked) => {
                            if (checked !== localShowConfigViolations) {
                              setLocalShowConfigViolations(checked);
                            }
                          }}
                          disabled={isWantedAdjustmentLoading}
                        />
                      </div>
                    </section>

                    <div
                      className={cn(
                        "animate-in fade-in-0 duration-300 transition-opacity relative mb-0 "
                      )}
                    >
                      <RosterMadeTable
                        key={`wanted-settings-${currentScheduleInfo.year}-${currentScheduleInfo.month}-${currentScheduleID}`}
                        defaultShiftCode={wantedStatsShiftCode}
                        year={currentScheduleInfo.year}
                        month={currentScheduleInfo.month}
                        showManpowerButton={showManpowerButton}
                        manPower_monthly={manPower_monthly}
                        memo={memo}
                        setMemo={setMemo}
                        isEditableMemo={isEditableMemo}
                        copyNurses={
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          wantedStatsNurses as any
                        }
                        hideWantedToggle={true}
                        hideManpowerToggle={true}
                        hasPrevTail={!!prevTailData}
                        onSummaryMinimizedChange={
                          setIsRosterMadeSummaryMinimized
                        }
                        hideViolationToggleRow
                        hideViolationFloating
                        layoutConfig={wantedBoardLayoutConfig}
                        shiftLookupMaps={shiftLookupMaps}
                        rawValidateList={
                          isWantedAdjustmentLoading
                            ? null
                            : rawWantedValidateList
                        }
                        isValidating={isWantedValidating}
                        phase={wantedValidationPhase}
                        violationState={{
                          showManpowerViolations: localShowManpowerViolations,
                          showConfigViolations: localShowConfigViolations,
                          showWantedViolations: localShowWantedViolations,
                          toggleManpowerViolations: () =>
                            setLocalShowManpowerViolations((prev) => !prev),
                          toggleConfigViolations: () =>
                            setLocalShowConfigViolations((prev) => !prev),
                          toggleWantedViolations: () =>
                            setLocalShowWantedViolations((prev) => !prev),
                        }}
                        role={
                          isAdmin
                            ? "admin"
                            : isHeadNurse
                              ? "head_nurse"
                              : "nurse"
                        }
                        showPrecepteeBadge={
                          wantedPrecepteePolicy.excludePrecepteeFromCount
                        }
                      />
                    </div>
                    <RosterViolationFloatingControls
                      rawValidateList={
                        isWantedAdjustmentLoading ? null : rawWantedValidateList
                      }
                      isValidating={isWantedValidating}
                      phase={wantedValidationPhase}
                      hideManpowerToggle
                      violationState={{
                        showManpowerViolations: localShowManpowerViolations,
                        showConfigViolations: localShowConfigViolations,
                        showWantedViolations: localShowWantedViolations,
                        toggleManpowerViolations: () =>
                          setLocalShowManpowerViolations((prev) => !prev),
                        toggleConfigViolations: () =>
                          setLocalShowConfigViolations((prev) => !prev),
                        toggleWantedViolations: () =>
                          setLocalShowWantedViolations((prev) => !prev),
                      }}
                    />
                  </div>

                  <RosterCreateTableHeader
                    defaultShiftCode={defaultShiftCode}
                    copyNurses={wantedCopyNurses}
                    setCopyNurses={setWantedCopyNurses}
                    onSortedRows={sortWantedBoardNurseRows}
                    currentYear={currentScheduleInfo.year}
                    currentMonth={currentScheduleInfo.month}
                    holidayDates={holidayDates ?? []}
                    prevTailData={prevTailData}
                    showPerShiftStats={showPerShiftStats}
                    setShowPerShiftStats={setShowPerShiftStats}
                    rosterCreateShiftLegend={wantedModalShiftLegend}
                    selectionFocusOnClick={false}
                    layoutConfig={wantedBoardLayoutConfig}
                  />
                </div>
              </div>

              {/* body — 관리보드 설정 탭은 헤더 그리드와 직접 맞추기 위해 px-5를 제거 */}
              <div className={cn("relative mx-auto w-max px-5")}>
                <ModifiedCellsProvider value={WANTED_BOARD_MODIFIED_CELLS}>
                  <TableBody
                    mode="CREATE"
                    copyNurses={displayedWantedCopyNurses}
                    setCopyNurses={setWantedCopyNurses}
                    availableShifts={shiftInfo}
                    isEditMode={true} // 원티드 반영 설정에서는 편집 모드
                    selectedShift={
                      isWantedAdjustmentToolOpen &&
                      wantedAdjustmentToolMode === "assignment"
                        ? selectedShift
                        : null
                    }
                    compactVerticalPadding
                    selectionFocusOnClick={false}
                    allowAssignmentLockedCellInteraction={
                      isWantedAdjustmentToolOpen &&
                      wantedAdjustmentToolMode === "banned" &&
                      (selectedBannedShifts.length > 0 || isClearingBannedShift)
                    }
                    modDayShift={(nurseId: string, scheduleDay: number) => {
                      const nurse = displayedWantedCopyNurses.find(
                        (item) => String(item.nurse_id) === nurseId
                      );

                      if (nurse) {
                        const day = scheduleDay + 1; // 0-based -> 1-based
                        const shiftId = nurse.schedule?.[scheduleDay] || null;
                        handleWantedCellClick(
                          nurse.nurse_id,
                          day,
                          shiftId || undefined
                        );
                      }
                    }}
                    saveNurseSequence={async () => {}} // 읽기 전용
                    currentScheduleInfo={currentScheduleInfo}
                    currentScheduleID={currentScheduleID}
                    isDragEditing={false} // 읽기 전용
                    selectedGroupId={selectedGroupId}
                    manPower_monthly={manPower_monthly}
                    manPower_daily={manPower_daily}
                    enableRowDragSort={false}
                    enableRowSequenceReorder
                    onMoveNurseRow={moveWantedBoardNurseRow}
                    onlyView={false} // 원티드 반영 설정에서는 클릭 가능
                    year={currentScheduleInfo.year}
                    month={currentScheduleInfo.month}
                    selectIdForSideprofile={undefined}
                    disableManpowerValidation={true} // 최소인원 검증 비활성화 (원티드 요청 현황에서는 불필요)
                    prevTailData={prevTailData}
                    showPerShiftStats={showPerShiftStats}
                    violationVisibilityOverride={{
                      showConfigViolations: localShowConfigViolations,
                      showManpowerViolations: localShowManpowerViolations,
                    }}
                    schedulePayloadYear={currentScheduleInfo.year}
                    schedulePayloadMonth={currentScheduleInfo.month}
                    currentGroupId={wantedBoardGroupId}
                    layoutConfig={wantedBoardLayoutConfig}
                    monthlyMemoByNurse={monthlyMemoByNurse}
                  />
                </ModifiedCellsProvider>
              </div>
            </div>
          </div>
        </div>
        {/* 설정완료 확인 모달 */}
        <Dialog
          open={isConfirmModalOpen}
          onOpenChange={(open) =>
            !open && !isUpdatingWantedAdjustment && setIsConfirmModalOpen(false)
          }
        >
          <DialogContent
            overlayClassName="bg-black/45"
            className="z-[150] w-[min(420px,calc(100vw-40px))] gap-0 rounded-xl border-0 bg-white p-0 font-pretendard shadow-[0_20px_60px_rgba(15,23,42,0.26)]"
          >
            <div className="px-6 py-5">
              <DialogTitle className="text-lg font-bold text-[#101828]">
                원티드 관리보드 설정을 저장하시겠습니까?
              </DialogTitle>
              <DialogDescription className="mt-3 text-sm font-semibold leading-relaxed text-[#667085]">
                저장한 설정은 원티드 반영 상태에 즉시 적용됩니다.
              </DialogDescription>
            </div>
            <div className="flex justify-end gap-2 border-t border-[#E4E7EC] px-6 py-4">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                disabled={isUpdatingWantedAdjustment}
                className="h-10 rounded-md bg-[#EEF2F6] px-5 text-sm font-bold text-[#344054] transition hover:bg-[#E4EAF1] disabled:cursor-not-allowed disabled:opacity-60"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsConfirmModalOpen(false);
                  updateWantedAdjustment();
                }}
                disabled={isUpdatingWantedAdjustment}
                className="h-10 rounded-md bg-sig px-5 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                저장
              </button>
            </div>
          </DialogContent>
        </Dialog>
        {/* 닫기 확인 모달 */}
        <Dialog
          open={isCloseConfirmModalOpen}
          onOpenChange={(open) => !open && setIsCloseConfirmModalOpen(false)}
        >
          <DialogContent
            overlayClassName="bg-black/45"
            className="z-[150] w-[min(420px,calc(100vw-40px))] gap-0 rounded-xl border-0 bg-white p-0 font-pretendard shadow-[0_20px_60px_rgba(15,23,42,0.26)]"
          >
            <div className="px-6 py-5">
              <DialogTitle className="text-lg font-bold text-[#101828]">
                변경사항이 저장되지 않았습니다.
              </DialogTitle>
              <DialogDescription className="mt-3 text-sm font-semibold leading-relaxed text-[#667085]">
                저장하지 않고 원티드 관리보드 설정을 닫으시겠습니까?
              </DialogDescription>
            </div>
            <div className="flex justify-end gap-2 border-t border-[#E4E7EC] px-6 py-4">
              <button
                type="button"
                onClick={() => setIsCloseConfirmModalOpen(false)}
                className="h-10 rounded-md bg-[#EEF2F6] px-5 text-sm font-bold text-[#344054] transition hover:bg-[#E4EAF1]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsCloseConfirmModalOpen(false);
                  onClose();
                }}
                className="h-10 rounded-md bg-sig px-5 text-sm font-bold text-white transition hover:opacity-90"
              >
                닫기
              </button>
            </div>
          </DialogContent>
        </Dialog>
        <Activity
          mode={
            isWantedAdjustmentToolOpen && !isCloseConfirmModalOpen
              ? "visible"
              : "hidden"
          }
          name="wanted-adjustment-tool"
        >
          <ModalRosterModify
            modalClose={setIsWantedAdjustmentToolOpen}
            isOpen={
              isWantedAdjustmentToolOpen && !isCloseConfirmModalOpen
            }
            availableShifts={shiftInfo}
            selectedShift={selectedShift}
            setSelectedShift={(shift) => {
              setSelectedShift(shift);
              setIsClearingWantedShift(false);
            }}
            deleteAllShift={() => undefined}
            title={
              wantedAdjustmentToolMode === "banned"
                ? "금지 근무 선택"
                : "근무코드 선택"
            }
            tabMode={wantedAdjustmentToolMode}
            onTabModeChange={handleWantedAdjustmentToolModeChange}
            selectedShifts={selectedBannedShifts}
            onToggleShift={(shift) => {
              setSelectedBannedShifts((previous) => {
                const nextShiftIds = toggleBannedShiftIds(
                  previous.map((selected) => selected.shift_id),
                  shift.shift_id
                ).shiftIds;
                return nextShiftIds.map(
                  (shiftId) =>
                    previous.find((selected) => selected.shift_id === shiftId) ??
                    shift
                );
              });
              setIsClearingBannedShift(false);
            }}
            portalZIndex={activeFloatingPanel === "shift" ? 130 : 70}
            onActivate={() => setActiveFloatingPanel("shift")}
            portalTarget="[data-wanted-adjustment-focus-scope]"
            containerSelector="[data-wanted-adjustment-focus-scope]"
            targetRef={assignmentTriggerRef}
            positionStorageKey={`roster.wanted-adjustment.shift-picker.position.v2:${loginUser?.nurse_id ?? "current"}:${wantedBoardGroupId ?? "current"}`}
            clearSelectionLabel="없음 · 셀 비우기"
            isClearSelection={
              wantedAdjustmentToolMode === "banned"
                ? isClearingBannedShift
                : isClearingWantedShift
            }
            onClearSelection={() => {
              if (wantedAdjustmentToolMode === "banned") {
                setSelectedBannedShifts([]);
                setIsClearingBannedShift(true);
                return;
              }
              setSelectedShift(null);
              setIsClearingWantedShift(true);
            }}
            onResetSelection={() => {
              if (wantedAdjustmentToolMode === "banned") {
                setSelectedBannedShifts([]);
                setIsClearingBannedShift(false);
                return;
              }
              setSelectedShift(null);
              setIsClearingWantedShift(false);
            }}
            clearSelectionOnClose={false}
          />
        </Activity>
      </DialogContent>
    </Dialog>
  );
}

export default ModalRosterWanted;
